import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import * as Api from "@/api";
import ContentLoader from "@/design/components/ContentLoader";
import Dropzone from "@/design/components/Dropzone";
import PageTitle from "@/design/components/PageTitle";
import { disableSearch, enableSearch } from "@/store/reducers/ui";
import useSearch from "@/utils/hooks/useSearch";
import { toggleLoadingState } from "@/utils/utility";
import { viImage, viMusic } from "@sippy-platform/valkyrie";

import { Grid } from "@mui/material";

import ImageFile from "./Image";
import SoundFile from "./Sound";

function MediaList() {
  const dispatch = useDispatch();

  const pbx = useSelector((state) => state.domain.pbx);
  const souncStore = useSelector((state) => state.domain.sounds);
  const imageStore = useSelector((state) => state.domain.images);
  const searchValue = useSelector((state) => state.ui.searchValue);
  const progressBar = useSelector((state) => state.ui.progressBar);

  // Search every time groupList or searchQuery changes
  const soundResults = useSearch(souncStore.list, searchValue, ["name"]);
  const imageResults = useSearch(imageStore.list, searchValue, ["name"]);

  useEffect(() => {
    toggleLoadingState(true);
    Promise.all([Api.getSounds(), Api.getImages()]).finally(toggleLoadingState);
  }, []);

  useEffect(() => {
    dispatch(enableSearch());
  }, [dispatch]);
  useEffect(() => () => dispatch(disableSearch()), [dispatch]);

  // Upload files
  const [fileTasks, setFileTasks] = useState([]);

  const onDrop = useCallback(
    (acceptedFiles) => {
      acceptedFiles.forEach((file) => {
        if (fileTasks.find((task) => task.file.name === file.name)) return;

        const controller = new AbortController();

        setFileTasks((tasks) => [...tasks, { file, controller, progress: {}, error: false }]);

        if (file.type === "audio/wav" || file.type === "audio/x-wav" || file.type === "audio/wave") {
          Promise.resolve(Api.postSound(file, controller, setFileTasks))
            .then(() => {
              setFileTasks((tasks) => tasks.filter((task) => task.file.name !== file.name));

              Api.getSounds();
            })
            .catch((error) => {
              if (error === "Upload canceled") {
                setFileTasks((tasks) => tasks.filter((task) => task.file.name !== file.name));
              } else {
                setFileTasks((tasks) =>
                  tasks.map((task) => {
                    if (task.file.name === file.name) {
                      task.error = true;

                      return task;
                    }

                    return task;
                  }),
                );
              }
            });
        } else {
          Promise.resolve(Api.postImage(file, controller, setFileTasks))
            .then(() => {
              setFileTasks((tasks) => tasks.filter((task) => task.file.name !== file.name));

              Api.getImages();
            })
            .catch((error) => {
              if (error === "Upload canceled") {
                setFileTasks((tasks) => tasks.filter((task) => task.file.name !== file.name));
              } else {
                setFileTasks((tasks) =>
                  tasks.map((task) => {
                    if (task.file.name === file.name) {
                      task.error = true;

                      return task;
                    }

                    return task;
                  }),
                );
              }
            });
        }
      });
    },
    [fileTasks],
  );

  return (
    <>
      <title>{`Media library \u00b7 ${pbx.name} \u00b7 ${import.meta.env.SIPPY_APP_TITLE}`}</title>
      <Dropzone
        fileTasks={fileTasks}
        setFileTasks={setFileTasks}
        config={{
          onDrop,
          maxSize: 50,
          accept: {
            "image/png": [".png"],
            "image/jpeg": [".jpg"],
            "audio/wav": [".wav"],
          },
        }}
      />
      <PageTitle disableGutters>Sounds</PageTitle>
      {souncStore.meta.count && soundResults.length >= 1 ? (
        <Grid
          container
          id="sound-list"
          spacing={1}
          sx={{
            justifyContent: "flex-start",
            alignItems: "flex-start",
          }}
        >
          {soundResults.map((sound) => (
            <Grid key={sound.id} size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }}>
              <SoundFile file={sound} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <ContentLoader
          count={souncStore.meta.count}
          loading={progressBar}
          noItems="You don't have any sounds yet"
          noResults="No sounds in your current query"
          fetching="Getting your sounds"
          icon={viMusic}
        />
      )}
      <PageTitle disableGutters>Images</PageTitle>
      {imageStore.meta.count && imageResults.length >= 1 ? (
        <Grid
          container
          id="image-list"
          spacing={1}
          sx={{
            justifyContent: "flex-start",
            alignItems: "flex-start",
          }}
        >
          {imageResults.map((image) => (
            <Grid key={image.id} size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }}>
              <ImageFile file={image} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <ContentLoader
          count={imageStore.meta.count}
          loading={progressBar}
          noItems="You don't have any images yet"
          noResults="No images in your current query"
          fetching="Getting your images"
          icon={viImage}
        />
      )}
    </>
  );
}

export default MediaList;
